(() => {
  'use strict';

  const STORAGE_KEY = 'md_energeez_compare';
  const LEGACY_KEY = 'm-compare-products';
  const t = (key, fallback = '', replacements = {}) => window.MDT ? window.MDT(key, fallback, replacements) : fallback;


  // Behoud de actieve Shopify-taal/markt bij dynamische storefront-links.
  const localizeStorefrontUrl = (url) => {
    const value = String(url || '').trim();
    if (!value) return '';

    let parsed;
    try {
      parsed = new URL(value, window.location.origin);
    } catch (error) {
      return value;
    }

    if (parsed.origin !== window.location.origin) return value;

    const root = String(window.Shopify?.routes?.root || '/');
    const localizedRoot = root.endsWith('/') ? root : `${root}/`;
    const localizedRootPath = localizedRoot === '/' ? '' : localizedRoot.replace(/\/$/, '');

    if (
      localizedRootPath &&
      (parsed.pathname === localizedRootPath || parsed.pathname.startsWith(`${localizedRootPath}/`))
    ) {
      return `${parsed.pathname}${parsed.search}${parsed.hash}`;
    }

    const path = parsed.pathname.replace(/^\/+/, '');
    return `${localizedRoot}${path}${parsed.search}${parsed.hash}`;
  };

  const normalize = (value) => String(value ?? '')
    .replace(/\s+/g, ' ')
    .trim()
    .toLowerCase();

  const escapeHtml = (value) => String(value ?? '')
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  class MDComparePage {
    constructor(root) {
      this.root = root;
      this.rootUrl = String(root.dataset.rootUrl || window.Shopify?.routes?.root || '/');
      if (!this.rootUrl.endsWith('/')) this.rootUrl += '/';
      this.quoteUrl = localizeStorefrontUrl(root.dataset.quoteUrl || '/pages/offerte-aanvragen');
      this.maxProducts = Number(root.dataset.maxProducts || 3);
      this.products = [];
      this.onlyDifferences = false;
      this.loading = root.querySelector('[data-md-compare-loading]');
      this.loadingTimer = null;
      this.empty = root.querySelector('[data-md-compare-empty]');
      this.tableWrap = root.querySelector('[data-md-compare-table-wrap]');
      this.head = root.querySelector('[data-md-compare-head]');
      this.body = root.querySelector('[data-md-compare-body]');
      this.status = root.querySelector('[data-md-compare-status]');
      this.differenceBox = root.querySelector('[data-md-difference-box]');
      this.onlyDifferencesInput = root.querySelector('[data-md-only-differences]');
      this.bottom = root.querySelector('[data-md-compare-bottom]');
      this.bottomCount = root.querySelector('[data-md-bottom-count]');
      this.bottomItems = root.querySelector('[data-md-bottom-items]');
      this.bind();
      this.reload();
    }

    bind() {
      document.addEventListener('md:compare:change', () => this.reload());
      window.addEventListener('storage', (event) => {
        if (event.key === STORAGE_KEY || event.key === LEGACY_KEY) this.reload();
      });
      this.onlyDifferencesInput?.addEventListener('change', () => {
        this.onlyDifferences = this.onlyDifferencesInput.checked;
        this.applyDifferenceFilter();
      });

      this.root.addEventListener('click', (event) => {
        const groupButton = event.target.closest('[data-md-compare-group-toggle]');
        if (groupButton) {
          const group = groupButton.dataset.group;
          const collapsed = groupButton.getAttribute('aria-expanded') === 'false';
          groupButton.setAttribute('aria-expanded', String(collapsed));
          this.body.querySelectorAll(`[data-md-group-row="${CSS.escape(group)}"]`).forEach((row) => {
            row.hidden = !collapsed;
          });
          return;
        }

        const cartButton = event.target.closest('[data-md-compare-cart]');
        if (cartButton) {
          this.addToCart(cartButton);
        }
      });
    }

    getSelection() {
      const liveSelection = window.MDEnergeezCompare?.getItems?.();
      if (Array.isArray(liveSelection) && liveSelection.length) {
        return liveSelection.slice(0, this.maxProducts);
      }

      try {
        const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
        if (Array.isArray(stored?.items)) {
          return stored.items
            .filter((item) => item && item.handle)
            .slice(0, this.maxProducts)
            .map((item) => ({
              handle: String(item.handle),
              title: String(item.title || item.handle),
              image: String(item.image || ''),
              url: localizeStorefrontUrl(item.url || `/products/${item.handle}`),
            }));
        }
      } catch (error) {
        // Ga door naar de legacy-opslag.
      }

      try {
        const legacy = JSON.parse(window.localStorage.getItem(LEGACY_KEY) || '[]');
        if (Array.isArray(legacy)) {
          return legacy
            .filter(Boolean)
            .slice(0, this.maxProducts)
            .map((handle) => ({
              handle: String(handle),
              title: String(handle),
              image: '',
              url: localizeStorefrontUrl(`/products/${handle}`),
            }));
        }
      } catch (error) {
        // Geen bruikbare opgeslagen vergelijking.
      }

      return [];
    }

    async reload() {
      const selection = this.getSelection();
      if (!selection.length) {
        this.stopLoading();
        this.products = [];
        this.render();
        return;
      }

      this.startLoading();
      this.setStatus('');

      const loaded = await Promise.all(selection.slice(0, this.maxProducts).map(async (item) => {
        try {
          return await this.fetchProduct(item.handle);
        } catch (error) {
          return null;
        }
      }));

      const missingHandles = selection
        .filter((item, index) => !loaded[index])
        .map((item) => item.handle)
        .filter(Boolean);

      if (missingHandles.length) {
        this.pruneMissingSelection(missingHandles);
      }

      this.products = loaded.filter(Boolean);
      this.stopLoading();
      this.render();

      if (missingHandles.length) {
        this.setStatus(t('compare.staleProduct', 'A product is no longer available and has been removed from the comparison.'), 'error');
      }
    }

    async fetchProduct(handle) {
      const response = await fetch(`${this.rootUrl}products/${encodeURIComponent(handle)}?view=md-compare`, {
        headers: { Accept: 'text/html' },
        credentials: 'same-origin',
      });
      if (!response.ok) throw new Error(`Product ${handle} kon niet worden geladen.`);

      const html = await response.text();
      const doc = new DOMParser().parseFromString(html, 'text/html');
      const dataScript = doc.querySelector('[data-md-compare-product]');
      if (!dataScript) throw new Error(t('compare.dataMissing', 'Comparison data is missing.'));

      const product = JSON.parse(dataScript.textContent || '{}');
      product.url = localizeStorefrontUrl(product.url || `/products/${handle}`);
      product.specGroups = this.extractSpecGroups(product);
      return product;
    }


    pruneMissingSelection(handles) {
      const missing = new Set((handles || []).map((handle) => String(handle || '')).filter(Boolean));
      if (!missing.size) return;

      try {
        const stored = JSON.parse(window.localStorage.getItem(STORAGE_KEY) || 'null');
        if (stored && Array.isArray(stored.items)) {
          stored.items = stored.items.filter((item) => item && !missing.has(String(item.handle || '')));
          stored.updatedAt = Date.now();
          if (stored.items.length) {
            window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
          } else {
            window.localStorage.removeItem(STORAGE_KEY);
          }
        }
      } catch (error) {
        // Opschonen van opslag mag de vergelijkpagina nooit blokkeren.
      }

      try {
        const legacy = JSON.parse(window.localStorage.getItem(LEGACY_KEY) || '[]');
        if (Array.isArray(legacy)) {
          const cleaned = legacy.filter((handle) => !missing.has(String(handle || '')));
          if (cleaned.length) window.localStorage.setItem(LEGACY_KEY, JSON.stringify(cleaned));
          else window.localStorage.removeItem(LEGACY_KEY);
        }
      } catch (error) {
        // Legacy-opslag is alleen een compatibiliteitslaag.
      }

      // Houd ook de actieve vergelijkstate in dezelfde tab direct synchroon.
      window.MDEnergeezCompare?.pruneMissing?.([...missing]);
    }

    extractSpecGroups(product) {
      const groups = [];
      const groupMap = new Map();

      const add = (groupName, label, value) => {
        const cleanLabel = String(label || '').trim();
        const cleanValue = this.formatSpecValue(value);
        if (!cleanLabel || !cleanValue) return;
        const group = String(groupName || t('compare.specifications', 'Specifications')).trim() || t('compare.specifications', 'Specifications');
        const key = normalize(group);
        if (!groupMap.has(key)) {
          const entry = { name: group, items: [] };
          groupMap.set(key, entry);
          groups.push(entry);
        }
        const entry = groupMap.get(key);
        if (!entry.items.some((item) => normalize(item.label) === normalize(cleanLabel))) {
          entry.items.push({ label: cleanLabel, value: cleanValue });
        }
      };

      add(t('compare.general', 'General'), t('compare.brand', 'Brand'), product.vendor);
      add(t('compare.general', 'General'), t('compare.productType', 'Product type'), product.productType);
      add(t('compare.general', 'General'), t('compare.articleSku', 'Item number / SKU'), product.sku);
      if (product.feature1Label && product.feature1Value) add(t('compare.keyFeatures', 'Key features'), product.feature1Label, product.feature1Value);
      if (product.feature2Label && product.feature2Value) add(t('compare.keyFeatures', 'Key features'), product.feature2Label, product.feature2Value);

      const structured = product.structuredSpecs;
      if (structured) {
        this.readStructuredSpecs(structured, add);
      } else if (product.descriptionHtml) {
        this.readDescriptionTables(product.descriptionHtml, add);
      }

      return groups;
    }

    readStructuredSpecs(structured, add) {
      if (Array.isArray(structured?.groups)) {
        structured.groups.forEach((group) => {
          const name = group.name || group.title || t('compare.specifications', 'Specifications');
          const items = Array.isArray(group.items) ? group.items : [];
          items.forEach((item) => add(name, item.label || item.name, item.value));
        });
        return;
      }

      if (Array.isArray(structured)) {
        structured.forEach((item) => add(item.group || t('compare.specifications', 'Specifications'), item.label || item.name, item.value));
        return;
      }

      if (typeof structured === 'object') {
        Object.entries(structured).forEach(([groupName, fields]) => {
          if (fields && typeof fields === 'object' && !Array.isArray(fields)) {
            Object.entries(fields).forEach(([label, value]) => add(groupName, label, value));
          }
        });
      }
    }

    readDescriptionTables(html, add) {
      const wrapper = document.createElement('div');
      wrapper.innerHTML = html;
      const tables = [...wrapper.querySelectorAll('table')];

      tables.forEach((table, tableIndex) => {
        let heading = t('compare.specifications', 'Specifications');
        let sibling = table.previousElementSibling;
        while (sibling) {
          if (/^H[2-4]$/.test(sibling.tagName) && sibling.textContent.trim()) {
            heading = sibling.textContent.trim();
            break;
          }
          sibling = sibling.previousElementSibling;
        }
        if (tables.length > 1 && heading === t('compare.specifications', 'Specifications')) heading = t('compare.specGroup', 'Specifications {number}', { number: tableIndex + 1 });

        [...table.querySelectorAll('tr')].forEach((row) => {
          const cells = [...row.querySelectorAll('th,td')].map((cell) => cell.textContent.replace(/\s+/g, ' ').trim());
          if (cells.length >= 2 && cells[0] && cells.slice(1).join(' / ')) {
            add(heading, cells[0], cells.slice(1).join(' / '));
          }
        });
      });
    }

    formatSpecValue(value) {
      if (value == null || value === '') return '';
      if (typeof value === 'boolean') return value ? 'Ja' : 'Nee';
      if (Array.isArray(value)) return value.map((item) => this.formatSpecValue(item)).filter(Boolean).join(', ');
      if (typeof value === 'object') {
        if ('value' in value && 'unit' in value) return `${value.value} ${value.unit}`;
        return '';
      }
      return String(value).trim();
    }

    buildRowModel() {
      const groups = [];
      const groupIndex = new Map();

      this.products.forEach((product) => {
        product.specGroups.forEach((group) => {
          const groupKey = normalize(group.name);
          if (!groupIndex.has(groupKey)) {
            const target = { name: group.name, key: `group-${groupIndex.size}`, rows: [], rowIndex: new Map() };
            groupIndex.set(groupKey, target);
            groups.push(target);
          }
          const target = groupIndex.get(groupKey);
          group.items.forEach((item) => {
            const rowKey = normalize(item.label);
            if (!target.rowIndex.has(rowKey)) {
              const row = { label: item.label, key: `${target.key}-row-${target.rowIndex.size}`, values: new Map() };
              target.rowIndex.set(rowKey, row);
              target.rows.push(row);
            }
            target.rowIndex.get(rowKey).values.set(product.handle, item.value);
          });
        });
      });

      groups.forEach((group) => {
        group.rows.forEach((row) => {
          const values = this.products.map((product) => normalize(row.values.get(product.handle) || '—'));
          row.isDifferent = new Set(values).size > 1;
        });
      });

      return groups;
    }

    render() {
      const hasProducts = this.products.length > 0;
      this.empty.hidden = hasProducts;
      this.tableWrap.hidden = !hasProducts;
      this.differenceBox.hidden = this.products.length < 2;
      this.bottom.hidden = !hasProducts;

      if (!hasProducts) {
        this.head.innerHTML = '';
        this.body.innerHTML = '';
        this.renderBottom();
        return;
      }

      this.renderHead();
      this.renderBody();
      this.renderBottom();
      this.applyDifferenceFilter();
    }

    renderHead() {
      const productHeaders = this.products.map((product) => `
        <th scope="col" class="md-compare-page__product-head">
          <button type="button" class="md-compare-page__remove" data-md-compare-remove data-product-handle="${escapeHtml(product.handle)}" aria-label="${escapeHtml(t('compare.removeProduct', 'Remove {title}', { title: product.title }))}">×</button>
          <div class="md-compare-page__product-main">
            <span class="md-compare-page__product-image">${product.image ? `<img src="${escapeHtml(product.image)}" alt="" width="112" height="112">` : ''}</span>
            <span class="md-compare-page__product-copy">
              ${product.vendor ? `<small>${escapeHtml(product.vendor)}</small>` : ''}
              <strong>${escapeHtml(product.title)}</strong>
              ${product.sku ? `<span>${escapeHtml(t('compare.articleAbbr', 'Item no.'))} ${escapeHtml(product.sku)}</span>` : ''}
              <span class="md-compare-page__stock ${product.available ? 'is-available' : 'is-unavailable'}">${product.available ? t('compare.inStock', 'In stock') : t('compare.outOfStock', 'Out of stock')}</span>
            </span>
          </div>
          <div class="md-compare-page__product-price">${product.priceVaries ? `${escapeHtml(t('compare.from', 'From'))} ${escapeHtml(product.priceMinFormatted || product.priceFormatted || '')}` : escapeHtml(product.priceFormatted || '')}</div>
          <div class="md-compare-page__product-actions">
            <a href="${escapeHtml(product.url)}">${escapeHtml(t('compare.viewProduct', 'View product'))}</a>
            ${product.hasOnlyDefaultVariant
              ? `<button type="button" data-md-compare-cart data-variant-id="${escapeHtml(product.variantId)}" data-minimum-quantity="${escapeHtml(product.minimumQuantity || 1)}" data-quantity-increment="${escapeHtml(product.quantityIncrement || 1)}" ${product.available ? '' : 'disabled'}>${escapeHtml(t('compare.addToCart', 'Add to cart'))}</button>`
              : `<a href="${escapeHtml(product.url)}">${escapeHtml(t('compare.chooseOptions', 'Choose options'))}</a>`}
            <a href="${escapeHtml(this.quoteUrl)}?product_handle=${encodeURIComponent(product.handle)}${product.hasOnlyDefaultVariant ? `&variant_id=${encodeURIComponent(product.variantId)}` : ''}&quantity=${encodeURIComponent(product.minimumQuantity || 1)}">${escapeHtml(t('compare.quote', 'Request a quote'))}</a>
          </div>
        </th>
      `).join('');

      this.head.innerHTML = `
        <tr>
          <th scope="col" class="md-compare-page__spec-head"><strong>${escapeHtml(t('compare.specifications', 'Specifications'))}</strong><span>${escapeHtml(t('compare.productCount', '{count} products', { count: this.products.length }))}</span></th>
          ${productHeaders}
        </tr>
      `;
    }

    renderBody() {
      const groups = this.buildRowModel();
      this.body.innerHTML = groups.map((group) => {
        const rows = group.rows.map((row) => {
          const cells = this.products.map((product) => {
            const value = row.values.get(product.handle) || '—';
            return `<td class="${row.isDifferent ? 'is-different' : ''}">${escapeHtml(value)}</td>`;
          }).join('');
          return `
            <tr class="md-compare-page__spec-row${row.isDifferent ? ' is-different-row' : ' is-same-row'}" data-md-group-row="${escapeHtml(group.key)}" data-md-different="${row.isDifferent}">
              <th scope="row">${escapeHtml(row.label)}</th>
              ${cells}
            </tr>
          `;
        }).join('');

        return `
          <tr class="md-compare-page__group-row">
            <th colspan="${this.products.length + 1}">
              <button type="button" data-md-compare-group-toggle data-group="${escapeHtml(group.key)}" aria-expanded="true"><span aria-hidden="true">⌄</span>${escapeHtml(group.name)}</button>
            </th>
          </tr>
          ${rows}
        `;
      }).join('');
    }

    renderBottom() {
      if (!this.bottomCount || !this.bottomItems) return;
      this.bottomCount.textContent = t(this.products.length === 1 ? 'compare.selectedOne' : 'compare.selectedOther', '{count} products selected', { count: this.products.length });
      this.bottomItems.innerHTML = this.products.map((product) => `
        <span class="md-compare-page__bottom-item">
          ${product.image ? `<img src="${escapeHtml(product.image)}" alt="" width="38" height="38">` : ''}
          <span><strong>${escapeHtml(product.title)}</strong>${product.sku ? `<small>${escapeHtml(product.sku)}</small>` : ''}</span>
          <button type="button" data-md-compare-remove data-product-handle="${escapeHtml(product.handle)}" aria-label="${escapeHtml(t('compare.removeProduct', 'Remove {title}', { title: product.title }))}">×</button>
        </span>
      `).join('');
    }

    applyDifferenceFilter() {
      this.body.querySelectorAll('[data-md-different]').forEach((row) => {
        const groupHidden = row.hidden && row.closest('tbody');
        if (this.onlyDifferences && row.dataset.mdDifferent !== 'true') {
          row.style.display = 'none';
        } else if (!groupHidden) {
          row.style.display = '';
        } else {
          row.style.display = '';
        }
      });

      this.body.querySelectorAll('.md-compare-page__group-row').forEach((groupRow) => {
        if (!this.onlyDifferences) {
          groupRow.style.display = '';
          return;
        }
        let next = groupRow.nextElementSibling;
        let hasDifference = false;
        while (next && !next.classList.contains('md-compare-page__group-row')) {
          if (next.dataset.mdDifferent === 'true') hasDifference = true;
          next = next.nextElementSibling;
        }
        groupRow.style.display = hasDifference ? '' : 'none';
      });
    }

    async addToCart(button) {
      const variantId = Number(button.dataset.variantId);
      const minimumQuantity = Math.max(1, Number(button.dataset.minimumQuantity || 1));
      if (!variantId) return;
      button.disabled = true;
      const original = button.textContent;
      button.textContent = t('compare.adding', 'Adding…');

      try {
        const response = await fetch(`${this.rootUrl}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [{ id: variantId, quantity: minimumQuantity }] }),
        });
        const data = await response.json();
        if (!response.ok) throw new Error(data.description || data.message || t('compare.cartError', 'Adding to cart failed.'));
        button.textContent = `${t('compare.addedToCart', 'Added to cart')} ✓`;
        this.setStatus(t('compare.productAdded', 'Product added to the cart.'), 'success');
        await this.refreshThemeCart();
      } catch (error) {
        button.textContent = original;
        button.disabled = false;
        this.setStatus(error.message || t('compare.cartError', 'Adding to cart failed.'), 'error');
      }
    }

    async refreshThemeCart() {
      try {
        if (window.MinimogEvents && window.MinimogTheme && MinimogTheme.pubSubEvents) {
          window.MinimogEvents.emit(MinimogTheme.pubSubEvents.cartUpdate, { source: 'md-product-compare' });
        }
        const drawer = document.querySelector('m-cart-drawer');
        if (drawer && typeof drawer.onCartDrawerUpdate === 'function') {
          drawer.onCartDrawerUpdate();
          window.setTimeout(() => drawer.open?.(), 240);
          return;
        }
        const cart = await fetch(`${this.rootUrl}cart.js`, { headers: { Accept: 'application/json' } }).then((response) => response.json());
        document.querySelectorAll('.m-cart-count-bubble').forEach((count) => {
          count.textContent = String(cart.item_count);
          count.classList.toggle('m:hidden', cart.item_count === 0);
        });
      } catch (error) {
        // De normale winkelwagenlink blijft beschikbaar als fallback.
      }
    }

    startLoading() {
      window.clearTimeout(this.loadingTimer);
      this.setLoading(false);
      this.root.setAttribute('aria-busy', 'true');
      this.loadingTimer = window.setTimeout(() => {
        this.loadingTimer = null;
        this.setLoading(true);
      }, 450);
    }

    stopLoading() {
      window.clearTimeout(this.loadingTimer);
      this.loadingTimer = null;
      this.setLoading(false);
      this.root.setAttribute('aria-busy', 'false');
    }

    setLoading(value) {
      if (this.loading) this.loading.hidden = !value;
    }

    setStatus(message, type = '') {
      if (!this.status) return;
      this.status.textContent = message;
      this.status.classList.toggle('is-error', type === 'error');
      this.status.classList.toggle('is-success', type === 'success');
    }
  }

  const init = () => {
    document.querySelectorAll('[data-md-compare-page]').forEach((root) => {
      if (root.dataset.mdCompareReady === 'true') return;
      root.dataset.mdCompareReady = 'true';
      new MDComparePage(root);
    });
  };

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init, { once: true });
  else init();
  document.addEventListener('shopify:section:load', init);
})();
